import { Alert, Button, Card, Divider, Dropdown, Input, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useSettings } from '../../../../context/settings-context';
import { useCurrencyList } from '../../../../hooks/use-currency-list';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import { ResetSettingsButton } from './reset-settings-button';

export function OrderTotalLimitsCard() {
  const form = useCustomerGroupBuilderForm();
  const settings = useSettings();
  const baseCurrency = settings.baseCurrency;
  const currencyOptions = useCurrencyList(baseCurrency);

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="inbox">
        {SETTING_KEY_TO_LABEL.orderTotalsValidation}
      </Card.Header>
      <form.Field name="settings.orderTotalsValidation">
        {(field) => (
          <Card.Body>
            <Text variant="secondary">
              {t('features:customer_group.components.order_total_limits_card.description')}{' '}
              <LearnMoreLink link="https://docs.sparklayer.io/customers#HfiQ1" />
            </Text>
            {field.state.value?.map((limit, index) => {
              return (
                <fieldset
                  // biome-ignore lint/suspicious/noArrayIndexKey: currency code can be empty or duplicated before selection
                  key={`${limit.currency_code}-${index}`}
                  className="flex flex-col items-stretch justify-between gap-3"
                >
                  <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                    <form.Field name={`settings.orderTotalsValidation[${index}].currency_code`}>
                      {(subField) => (
                        <Dropdown
                          options={currencyOptions}
                          label={t('labels:currency')}
                          onChange={subField.handleChange}
                          value={subField.state.value ?? ''}
                        />
                      )}
                    </form.Field>
                    <form.Field name={`settings.orderTotalsValidation[${index}].minimum_value`}>
                      {(subField) => (
                        <Input
                          label={t(
                            'features:customer_group.components.order_total_limits_card.min_value.label',
                          )}
                          value={subField.state.value ?? ''}
                          placeholder={(0).toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                          })}
                          error={subField.state.meta.errors[0]?.message}
                          onChange={subField.handleChange}
                        />
                      )}
                    </form.Field>
                    <form.Field name={`settings.orderTotalsValidation[${index}].maximum_value`}>
                      {(subField) => (
                        <Input
                          label={t(
                            'features:customer_group.components.order_total_limits_card.max_value.label',
                          )}
                          placeholder={t(
                            'features:customer_group.components.order_total_limits_card.max_value.placeholder',
                          )}
                          value={subField.state.value ?? ''}
                          error={subField.state.meta.errors[0]?.message}
                          onChange={subField.handleChange}
                        />
                      )}
                    </form.Field>
                    {/* Conditionally render the buttons with/without text depending on the screen size */}
                    <span className="sm:hidden">
                      <Button
                        icon="delete"
                        variant="dangerOutline"
                        onClick={() => field.removeValue(index)}
                      >
                        {t('buttons:delete')}
                      </Button>
                    </span>
                    <span className="hidden sm:mt-6.5 sm:inline">
                      <Button
                        icon="delete"
                        variant="dangerOutline"
                        onClick={() => field.removeValue(index)}
                      />
                    </span>
                  </div>
                  {index < (field.state.value?.length ?? 0) - 1 && (
                    <span className="my-2">
                      <Divider />
                    </span>
                  )}
                </fieldset>
              );
            })}
            {!field.state.meta.isValid && (
              <Alert
                variant="warning"
                title={t('features:customer_group.components.order_total_limits_card.alert.title')}
                headingLevel="h3"
              >
                {field.state.meta.errors[0].message}
              </Alert>
            )}
            <div className="flex justify-start gap-3">
              <Button
                onClick={() =>
                  field.pushValue({
                    currency_code: baseCurrency,
                    minimum_value: '0.0',
                    compare_type: 'net',
                  })
                }
                variant="secondary"
              >
                {t('features:customer_group.components.order_total_limits_card.add')}
              </Button>
              <ResetSettingsButton field={field} />
            </div>
          </Card.Body>
        )}
      </form.Field>
    </Card>
  );
}
